/**
 * ⟦C57 · lot 3 · A-174⟧ **Les paliers, et les « vrais chiffres du vrai YouTube » qu'ils portent.**
 *
 * ⟦A-4 décision 13⟧ : « Aux paliers, des pop-ups "bravo" avec les VRAIS pourcentages du YouTube
 * réel. » Les chiffres viennent de [RECHERCHE-NARRATIF §2.16](../../../docs/RECHERCHE-NARRATIF.md),
 * sourcés 2025-2026 : ils ne sont ni arrondis à la louche ni inventés, et c'est tout l'intérêt, le
 * jeu parle du vrai YouTube et le joueur mesure ce qu'il vient de faire.
 *
 * La table vivait dans `components/PopupsPalier.tsx`. Elle est déplacée ici parce que DEUX
 * surfaces la lisent : la pop-up du palier (une phrase courte à l'écran) et l'entrée du Journal
 * (`game/journal.ts`, qui garde le texte entier derrière « Les dessous »).
 *
 * **La règle d'A-174** : à l'écran, le chiffre en gros et UNE phrase de 12 mots au plus (`court`).
 * Le texte long (`texte`) n'est PAS réécrit : il reste tel quel, mot pour mot, et s'ouvre par le
 * bouton « Les dessous » de la pop-up, puis par celui de l'entrée du Journal. Rien ne disparaît.
 *
 * Pur : aucune dépendance au DOM ni aux stores.
 */

export interface ChiffrePalier {
  icone: string;
  /** Le titre de la plaque : « 100 abonnés ». */
  titre: string;
  /** Le texte COMPLET, aux chiffres réels. Intact : il s'ouvre dans « Les dessous ». */
  texte: string;
  /** ⟦C57⟧ L'unique phrase de la pop-up : 12 mots au plus. Écrite à côté du long, jamais à sa place. */
  court: string;
}

/** RECHERCHE-NARRATIF §2.16 : un texte par palier, aux chiffres réels, et sa forme courte. */
export const CHIFFRES_PALIERS: Readonly<Record<number, ChiffrePalier>> = {
  100: {
    icone: "💯",
    titre: "100 abonnés",
    texte:
      "La majorité des chaînes du monde n'a jamais dépassé zéro. Toi, si. Sur 115 millions de chaînes, l'immense majorité est vide ou morte — tu viens d'en sortir.",
    court: "La plupart des chaînes du monde n'y arrivent jamais.",
  },
  1000: {
    icone: "🚪",
    titre: "1 000 abonnés",
    texte:
      "9 chaînes sur 10 ne franchiront JAMAIS cette ligne — 40,6 % des chaînes actives seulement y arrivent, et il leur faut 15,5 mois en moyenne. Tu ne te rends pas compte.",
    court: "9 chaînes sur 10 ne franchiront jamais cette ligne.",
  },
  10000: {
    icone: "🎯",
    titre: "10 000 abonnés",
    texte: "Top 8 % des chaînes actives du monde. Sérieusement. 7,9 %, pour être exact.",
    court: "Top 8 % des chaînes actives du monde.",
  },
  100000: {
    icone: "🥈",
    titre: "100 000 abonnés",
    texte:
      "Une chaîne sur deux cents. 0,54 % du monde entier — environ 619 000 chaînes sur 115 millions. La plaque arrive.",
    court: "Une chaîne sur deux cents seulement : la plaque arrive.",
  },
  500000: {
    icone: "📈",
    titre: "500 000 abonnés",
    texte:
      "Tu es entre le club des 100k et celui du million — moins d'une chaîne sur 500 dans le monde.",
    court: "Moins d'une chaîne sur 500 dans le monde.",
  },
  1000000: {
    icone: "🥇",
    titre: "LE MILLION",
    texte:
      "~70 000 chaînes sur 115 millions. 0,06 %. Une rue à ton nom, franchement, ce n'est pas volé.",
    court: "0,06 % des chaînes : une rue à ton nom, pas volée.",
  },
  2000000: {
    icone: "🌍",
    titre: "2 millions",
    texte: "Tu es passé de l'autre côté. Il n'y a plus de carte.",
    court: "De l'autre côté, il n'y a plus de carte.",
  },
  5000000: {
    icone: "🌍",
    titre: "5 millions",
    texte: "À ce niveau-là, on ne compte plus en chaînes, on compte en pays.",
    court: "On ne compte plus en chaînes, mais en pays.",
  },
  10000000: {
    icone: "💎",
    titre: "10 millions",
    texte:
      "0,002 % des chaînes du monde. Environ 2 600 sur Terre. Le bouton des 100 M, lui, est fabriqué à Baccarat — au cas où.",
    court: "0,002 % des chaînes : environ 2 600 sur Terre.",
  },
};

/**
 * Le chiffre d'un palier. Un palier que la table ne connaît pas (le moteur en ajoute un un jour)
 * retombe sur une plaque neutre : jamais un trou, jamais une erreur.
 */
export function chiffreDuPalier(palier: number, formater: (n: number) => string): ChiffrePalier {
  return (
    CHIFFRES_PALIERS[palier] ?? {
      icone: "🏆",
      titre: `${formater(palier)} abonnés`,
      texte: "Palier franchi.",
      court: "Palier franchi.",
    }
  );
}
